const groupPrefix = createApiRoute(ApiModule.PRODUCTION_PLANNING, ApiModule.EMPLOYEE)

export const apiTabletPlans = {
	getTabletInstructions: async (
		instructionType: EInstructionTabletType,
		date: string,
		query?: string,
		filters?: Record<string, string | string[]>,
		page: number = 1,
		perPage?: number,
	) => {
		const data: any = await apiCommon<{ data: []; query_count?: number; stages?: any[]; pagination?: any }>(
			`${groupPrefix}/tablet/instructions/${instructionType}`,
			{
				method: 'GET',
				query: {
					date,
					...(query && { query }),
					...filters,
					page,
					...(perPage !== undefined && { per_page: perPage }),
				},
			},
		)

		return {
			data: data.data,
			query_count: (data.query_count || data.matches_count) ?? 0,
			stages: data.stages,
			pagination: data.pagination,
			socket_ids: data.socket_ids ?? null,
			place: data.place ?? null,
		}
	},

	getProductionSocketsBySimulation: async (simulationId: number) => {
		const { data } = await apiCommon<any[]>(`${groupPrefix}/production-sockets/by-simulation/${simulationId}`, {
			method: 'GET',
		})
		return { data }
	},

	getPositionsInstructions: async (
		positionsIds: Record<string, number[]>,
		simulationID: number,
		incrementIds?: number[],
		query?: string,
		place?: string | null,
		selectedRecipes?: number[] | null,
	) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-instructions`, {
			method: 'POST',
			body: {
				positions_by_language: positionsIds,
				simulation_id: simulationID,
				increment_ids: incrementIds,
				...(query && { query }),
				...(place && { place }),
				...(Array.isArray(selectedRecipes) && { selected_recipes: selectedRecipes }),
			},
		})
		return { data }
	},

	getPositionsInstructionsRecipe: async (
		positionsIds: Record<string, number[]>,
		simulationID: number,
		incrementIds?: number[],
		query?: string,
		place?: string | null,
		selectedRecipes?: number[] | null,
	) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-instructions-recipe`, {
			method: 'POST',
			body: {
				positions_by_language: positionsIds,
				simulation_id: simulationID,
				increment_ids: incrementIds,
				...(query && { query }),
				...(place && { place }),
				...(Array.isArray(selectedRecipes) && { selected_recipes: selectedRecipes }),
			},
		})
		return { data }
	},

	getPositionsInstructionsMeat: async (
		positionsIds: Record<string, number[]>,
		simulationID: number,
		incrementIds?: number[],
		query?: string,
		place?: string | null,
		selectedRecipes?: number[] | null,
	) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-instructions-meat`, {
			method: 'POST',
			body: {
				positions_by_language: positionsIds,
				simulation_id: simulationID,
				increment_ids: incrementIds,
				...(query && { query }),
				...(place && { place }),
				...(Array.isArray(selectedRecipes) && { selected_recipes: selectedRecipes }),
			},
		})
		return { data }
	},

	getPositionsInstructionsFlourDessert: async (
		positionsIds: Record<string, number[]>,
		simulationID: number,
		incrementIds?: number[],
		query?: string,
		place?: string | null,
		selectedRecipes?: number[] | null,
	) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-instructions-flour-dessert`, {
			method: 'POST',
			body: {
				positions_by_language: positionsIds,
				simulation_id: simulationID,
				increment_ids: incrementIds,
				...(query && { query }),
				...(place && { place }),
				...(Array.isArray(selectedRecipes) && { selected_recipes: selectedRecipes }),
			},
		})
		return { data }
	},

	getPositionsInstructionsKitchen: async (
		positionsIds: Record<string, number[]>,
		simulationID: number,
		incrementIds?: number[],
		query?: string,
		place?: string | null,
		selectedRecipes?: number[] | null,
	) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-instructions-kitchen`, {
			method: 'POST',
			body: {
				positions_by_language: positionsIds,
				simulation_id: simulationID,
				increment_ids: incrementIds,
				...(query && { query }),
				...(place && { place }),
				...(Array.isArray(selectedRecipes) && { selected_recipes: selectedRecipes }),
			},
		})
		return { data }
	},

	selectRecipes: async (simulationId: number, groupedTaskIds: string[], concurrentWorkGroupId?: string | null) => {
		const data = await apiCommon<{
			status: 'ok'
			data: Array<{
				grouped_task_id: string
				concurrent_work_group_id: string | null
				status: string
				started_at: string | null
				paused_at: string | null
				completed_at: string | null
			}>
		}>(`${groupPrefix}/tablet/instructions/select-recipes`, {
			method: 'POST',
			body: {
				simulation_id: simulationId,
				grouped_task_ids: groupedTaskIds,
				...(concurrentWorkGroupId !== undefined && { concurrent_work_group_id: concurrentWorkGroupId }),
			},
		})
		return { data: data.data }
	},

	getMealInstructions: async (
		mealsByLanguage: Record<string, number[]>,
		simulationID: number,
		productionTasks: boolean = false,
		_query?: string,
	) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-meal-instructions`, {
			method: 'POST',
			body: {
				meals_by_language: mealsByLanguage,
				simulation_id: simulationID,
				...(productionTasks ? { production_tasks: true } : {}),
			},
		})
		return { data }
	},

	getPackagingInstructions: async (simulationID: number, mealID: number) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/packaging-details`, {
			method: 'GET',
			query: { simulation_id: simulationID, meal_id: mealID },
		})
		return { data }
	},

	getMealPackagingInstructions: async (
		mealsByLanguage: Record<string, number[]>,
		simulationID: number,
		query?: string,
	) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-meal-packaging-instructions`, {
			method: 'POST',
			body: { meals_by_language: mealsByLanguage, simulation_id: simulationID, ...(query && { query }) },
		})
		return { data }
	},

	getAllMealPackagingInstructions: async (simulationID: number) => {
		const data = await apiCommon<[]>(`${groupPrefix}/tablet/instructions/view-all-meal-packaging-instructions`, {
			method: 'POST',
			body: { simulation_id: simulationID },
		})
		return { data }
	},

	getWarehouseCategories: async (date: Date) => {
		const { data } = await apiCommon<string[][]>(`${groupPrefix}/tablet/instructions/warehouse-categories`, {
			method: 'GET',
			query: {
				date: dateToFilterFormat(date),
			},
		})

		return {
			data: (data[0] ?? []).map(entry => entry.trim()).filter(Boolean),
		}
	},

	updateRecipeStatus: async (payload: { recipe_task_id: string; checked: boolean; place?: string | null }) => {
		await apiCommon(`${groupPrefix}/tablet/instructions/update-recipe-status`, {
			method: 'POST',
			body: {
				recipe_task_id: payload.recipe_task_id,
				checked: payload.checked,
				place: payload.place ?? null,
			},
		})
	},

	updateMealStatus: async (payload: { meal_task_id: string; checked: boolean; place?: string | null }) => {
		await apiCommon(`${groupPrefix}/tablet/instructions/update-meal-status`, {
			method: 'POST',
			body: {
				meal_task_id: payload.meal_task_id,
				checked: payload.checked,
				place: payload.place ?? null,
			},
		})
	},

	updateVariantStatus: async (payload: {
		variant_task_id: string
		checked: boolean
		packed_quantity?: number
		place?: string | null
	}) => {
		await apiCommon(`${groupPrefix}/tablet/instructions/update-variant-task-status`, {
			method: 'POST',
			body: {
				variant_task_id: payload.variant_task_id,
				checked: payload.checked,
				...(payload.packed_quantity !== undefined && { packed_quantity: payload.packed_quantity }),
				place: payload.place ?? null,
			},
		})
	},

	updateLogisticTaskStatus: async (payload: {
		logistic_task_ids: string[]
		checked: boolean
		quantity?: number
		place?: string | null
	}) =>
		await apiCommon(`${groupPrefix}/tablet/instructions/update-logistic-task-status`, {
			method: 'POST',
			body: {
				logistic_task_ids: payload.logistic_task_ids,
				checked: payload.checked,
				quantity: payload.quantity,
				place: payload.place ?? null,
			},
		}),

	updatePackagingReceivedQuantity: async (payload: {
		simulation_id: number
		logistic_task_ids: string[]
		quantity: number
		place: string
	}) =>
		await apiCommon<{ status: string }>(`${groupPrefix}/tablet/instructions/update-packaging-received-quantity`, {
			method: 'POST',
			body: {
				simulation_id: payload.simulation_id,
				logistic_task_ids: payload.logistic_task_ids,
				quantity: payload.quantity,
				place: payload.place,
			},
		}),

	updateScheduleTaskStatus: async (payload: {
		schedule_task_id: number
		checked: boolean
		is_kitchen_request?: boolean
		place?: string | null
	}) =>
		await apiCommon<{ status: string; data: any }>(
			`${groupPrefix}/tablet/instructions/update-schedule-task-status`,
			{
				method: 'POST',
				body: {
					schedule_task_id: payload.schedule_task_id,
					checked: payload.checked,
					...(payload.is_kitchen_request ? { is_kitchen_request: true } : {}),
					place: payload.place ?? null,
				},
			},
		),

	updateQualityScore: async (payload: {
		quality_task_ids: string[]
		received_weight: number
		score: string
		checked: true
		place: string
	}) =>
		await apiCommon<{ status: string; data: any[] }>(`${groupPrefix}/tablet/instructions/update-quality-score`, {
			method: 'POST',
			body: {
				quality_task_ids: payload.quality_task_ids,
				received_weight: payload.received_weight,
				score: payload.score,
				checked: true,
				place: payload.place,
			},
		}),

	confirmProducts: async (payload: { grouped_task_ids: string[]; is_packaging_request?: boolean; place: string }) =>
		await apiCommon<{ status: string; data: any }>(`${groupPrefix}/tablet/instructions/confirm-products`, {
			method: 'POST',
			body: {
				grouped_task_ids: payload.grouped_task_ids,
				...(payload.is_packaging_request ? { is_packaging_request: true } : {}),
				place: payload.place,
			},
		}),

	startGroupedTaskFromWaiting: async (payload: {
		grouped_task_ids: string[]
		is_packaging_request?: boolean
		place: string
	}) =>
		await apiCommon<{ status: string; data: any }>(
			`${groupPrefix}/tablet/instructions/start-grouped-task-from-waiting`,
			{
				method: 'POST',
				body: {
					grouped_task_ids: payload.grouped_task_ids,
					...(payload.is_packaging_request ? { is_packaging_request: true } : {}),
					place: payload.place,
				},
			},
		),

	pauseGroupedTask: async (payload: { grouped_task_ids: string[]; place: string }) =>
		await apiCommon<{
			status: string
			data: {
				grouped_task_id: string
				status: string
				started_at: string | null
				paused_at: string | null
				completed_at: string | null
			}[]
		}>(`${groupPrefix}/tablet/instructions/pause-grouped-task`, {
			method: 'POST',
			body: {
				grouped_task_ids: payload.grouped_task_ids,
				place: payload.place,
			},
		}),

	resumeGroupedTask: async (payload: { grouped_task_ids: string[]; place: string }) =>
		await apiCommon<{
			status: string
			data: {
				grouped_task_id: string
				status: string
				started_at: string | null
				paused_at: string | null
				completed_at: string | null
			}[]
		}>(`${groupPrefix}/tablet/instructions/resume-grouped-task`, {
			method: 'POST',
			body: {
				grouped_task_ids: payload.grouped_task_ids,
				place: payload.place,
			},
		}),

	completeGroupedTask: async (payload: { grouped_task_ids: string[]; is_kitchen_request?: boolean; place: string }) =>
		await apiCommon<{
			status: string
			data: {
				grouped_task_id: string
				status: string
				started_at: string | null
				paused_at: string | null
				completed_at: string | null
			}[]
		}>(`${groupPrefix}/tablet/instructions/complete-grouped-task`, {
			method: 'POST',
			body: {
				grouped_task_ids: payload.grouped_task_ids,
				...(payload.is_kitchen_request ? { is_kitchen_request: true } : {}),
				place: payload.place,
			},
		}),

	getAvailableCuttingTypes: async (date: Date, socketIds?: number[]) => {
		const { data } = await apiCommon<{ id: string; name: string }[]>(
			`${groupPrefix}/tablet/instructions/available-cutting-types`,
			{
				method: 'GET',
				query: {
					date: dateToFilterFormat(date),
					...(socketIds?.length && { 'socket_ids[]': socketIds.map(String) }),
				},
			},
		)
		return { data }
	},

	getFilterRecipes: async (date: Date) => {
		const { data } = await apiCommon<[]>(`${groupPrefix}/simulations/available-semi-products`, {
			method: 'GET',
			query: {
				date: dateToFilterFormat(date),
			},
		})
		return { data }
	},

	mealUpdateFromCRM: async (idDetectedChanges: number) => {
		await apiCommon<{ message: string }>(`${groupPrefix}/production-meal-detected-changes/${idDetectedChanges}`, {
			method: 'POST',
		})
	},

	createQualityScoreComment: async (payload: {
		quality_task_ids: string[]
		simulation_id: number
		comment: string
		place: string
	}) => {
		const { data } = await apiCommon<any>(`${groupPrefix}/tablet/instructions/quality-score-comments`, {
			method: 'POST',
			body: {
				quality_task_ids: payload.quality_task_ids,
				simulation_id: payload.simulation_id,
				comment: payload.comment,
				place: payload.place,
			},
		})
		return { data }
	},

	fetchVariantAvailablePackagings: async (variantTaskId: string) => {
		const { data } = await apiCommon<IAvailablePackagingV2[]>(
			`${groupPrefix}/tablet/instructions/variant/${variantTaskId}/packagings/available`,
			{ method: 'GET' },
		)
		return { data }
	},

	setVariantPackaging: async (
		variantTaskId: string,
		payload: { type: 'replaced' | 'additional'; id: number | null; quantity: number; place: string },
	) =>
		await apiCommon<{ message: string; data: any }>(
			`${groupPrefix}/tablet/instructions/variant/${variantTaskId}/packaging`,
			{ method: 'POST', body: payload },
		),

	createGroupedTaskTemperatureLog: async (payload: {
		grouped_task_id: string
		simulation_id: number
		read_type: GroupedTaskTemperaturePhaseType
		temperature_celsius: number
		place: string
	}) => {
		const { data } = await apiCommon<GroupedTaskTemperatureLog>(
			`${groupPrefix}/tablet/instructions/grouped-task-temperature-logs`,
			{
				method: 'POST',
				body: {
					grouped_task_id: payload.grouped_task_id,
					simulation_id: payload.simulation_id,
					read_type: payload.read_type,
					temperature_celsius: payload.temperature_celsius,
					place: payload.place,
				},
			},
		)
		return { data }
	},

	deleteVariantPackaging: async (variantTaskId: string, productionMealPackagingId: number, place: string) =>
		await apiCommon<{ message: string }>(
			`${groupPrefix}/tablet/instructions/variant/${variantTaskId}/packaging/${productionMealPackagingId}`,
			{ method: 'DELETE', query: { place } },
		),
}
